import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { SectionNavBar } from './SectionNav';
import { ChatSidebar, AskSuryaIQButton } from '../ui/chat-drawer';
import { TourProvider } from '../tour/TourProvider';
import { TourPicker } from '../tour/TourPicker';
import { RulingsProvider } from '../rulings/RulingsProvider';
import { RulingsPill } from '../rulings/RulingsPill';
import { AppRail } from './AppRail';
import { StatusBar } from './StatusBar';
import { RulingsDesk } from '../rulings/RulingsDesk';
import { ContextPanelProvider, ContextPanelRow, useContextPanelWidth } from './ContextPanel';
// SPEC-506: the Feedback engine — pill, shortcut, snip overlay and panel, on every page.
import { FeedbackProvider } from '../feedback/FeedbackProvider';
// SPEC-563: the one shared copy of the per-user menu layout, and its Arrange dialog.
import { NavLayoutProvider } from '@/hooks/useNavLayout';
import { ArrangeMenuHost } from './ArrangeMenuDialog';
import { Menu, Eye, X } from 'lucide-react';
import { useFreshness } from '@/hooks/useFreshness';
import { useWorkspace } from '@/lib/workspace';
import { EnvironmentChip } from './EnvironmentChip';
import { useAuth } from '@/lib/auth';
import { findRegistryPage, visibleTabKeys } from '@/lib/viewRegistry';
import { topBarRightOffset } from '@/config/shell';

/**
 * SPEC-139 B2: when the desktop sidebar is collapsed the grouped tabs are
 * hidden, so converted pages get a horizontal section-nav strip here instead.
 * Registry-driven — same tabs/links/visibility as the sidebar SectionNav.
 */
function CollapsedSectionBar() {
  const location = useLocation();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const page = findRegistryPage(location.pathname);
  if (!page?.groups) return null;
  const activeKey = new URLSearchParams(location.search).get('tab') || '';
  const groups = page.groups.map((g) => ({
    label: g.label,
    items: g.tabs.map((t) => ({ key: t.key, label: t.label, advanced: t.advanced })),
  }));
  const visibleKeys = visibleTabKeys(page, workspace?.tabs?.[page.path], user?.tabs?.[page.path]);
  return (
    <SectionNavBar groups={groups} basePath={page.path} activeKey={activeKey} visibleKeys={visibleKeys} />
  );
}

/**
 * SPEC-515 — THE view picker (admin/super_admin), the only one in the app. It lives in the
 * account menu at the foot of the left rail. Picking a view previews it APP-WIDE: the merged
 * user carries the view's pages/tabs/capabilities AND its Overview block, so the navigation
 * and the Overview change in the same render. The choice persists (localStorage via the auth
 * context) until "My view (System)" is picked. Non-admins have no picker: their view is
 * assigned, and the Overview shows it automatically.
 */
function ViewPicker() {
  const { user, viewPreview, viewTemplates, setViewPreview } = useAuth();
  // Real role gates rendering — user.role is never overridden by preview.
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) return null;
  if (viewTemplates.length === 0) return null;
  return (
    <label
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs transition-colors ${
        viewPreview
          ? 'bg-amber-500/15 border-amber-500/40 text-amber-400'
          : 'bg-[var(--bg-raised)] border-[var(--border-subtle)] text-[var(--text-tertiary)]'
      }`}
      title={viewPreview
        ? `Previewing the ${viewPreview.label || viewPreview.name} view everywhere — pick "My view (System)" to exit`
        : 'Preview a view exactly as its users see it, on every page and on the Overview'}
    >
      <Eye className="w-3.5 h-3.5 shrink-0" />
      <select
        data-testid="view-picker"
        value={viewPreview?.id || ''}
        aria-label="View"
        onChange={(e) => setViewPreview(e.target.value || null)}
        className="bg-transparent outline-none cursor-pointer max-w-[170px] truncate text-inherit"
      >
        <option value="">My view (System)</option>
        {viewTemplates.map((t) => (
          <option key={t.id} value={t.id}>View: {t.label || t.name}</option>
        ))}
      </select>
    </label>
  );
}

/** While a preview is ACTIVE the top bar shows which view it is, amber, with a one-click exit
 *  — a status chip, not a second picker (SPEC-515: exactly one picker). */
function ViewPreviewChip() {
  const { viewPreview, setViewPreview } = useAuth();
  if (!viewPreview) return null;
  const label = viewPreview.label || viewPreview.name;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs bg-amber-500/15 border-amber-500/40 text-amber-400">
      <Eye className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span className="whitespace-nowrap">View: {label}</span>
      <button
        type="button"
        onClick={() => setViewPreview(null)}
        aria-label={`Stop previewing the ${label} view`}
        title="Back to my view"
        className="rounded hover:bg-amber-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}

/* SPEC-509: the top-right notification bell lived here. It merged into the rail's Alerts
 * button (one badge, never a sum) and its list became the "Updates" tab on the Alerts page
 * (hooks/useUpdates.ts). The walk-through button moved to the rail's foot cluster too. */

/* SPEC-462: on desktop the sync time lives in the bottom StatusBar (system state). This chip
 * now renders on MOBILE only, where there is no status bar. The freshness state is read ONCE
 * in AppShell and passed in, so the two consumers do not each poll the endpoint. */
function SyncStatusIndicator({ freshness }: { freshness: ReturnType<typeof useFreshness> }) {
  const { syncAge, syncStatus, lastSync, criticalStale, demandSignalStale, stockSignalStale } = freshness;
  if (!lastSync) return null;

  // SPEC-102: colour from the backend critical-feed verdict, not a local sync-age
  // threshold. A frozen demand/stock feed turns the whole app's indicator red even
  // if the portal is still syncing on schedule; a pipeline error degrades to amber.
  const dotColor = criticalStale ? 'bg-red-400' : syncStatus === 'error' ? 'bg-amber-400' : 'bg-green-400';
  const textColor = criticalStale ? 'text-red-400' : syncStatus === 'error' ? 'text-amber-400' : 'text-green-400';
  const feedNote = criticalStale
    ? `\nStale feed: ${[demandSignalStale && 'demand signal', stockSignalStale && 'stock signal'].filter(Boolean).join(', ')}`
    : '';

  return (
    <Link
      to="/governance?tab=freshness"
      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[var(--text-tertiary)] hover:bg-[var(--bg-overlay)] transition-colors"
      title={`Portal sync: ${syncStatus === 'ok' ? 'Healthy' : syncStatus === 'error' ? 'Error' : 'Unknown'}\nLast sync: ${lastSync?.replace('T', ' ').slice(0, 16) || 'Unknown'}${feedNote}`}
    >
      <span className={`w-2 h-2 rounded-full ${dotColor}`} />
      <span className={`text-xs font-mono ${textColor}`}>{syncAge}</span>
    </Link>
  );
}

/* SPEC-514: the All/Produced/Imported toggle left the top bar. It renders under the page
 * title, in PageHeader's `controls` slot, on the pages that use it
 * (components/ProductTypeToggle.tsx — same SPEC-147 gating, same shared context). */

/** SPEC-514 — the top bar's right cluster: ONE fixed row, `[staging chip · workspace · view
 *  preview chip · Ask SuryaIQ]`, placed LEFT of the docked "About this screen" panel (open or as its strip)
 *  and of the open chat drawer, so it never covers the panel or its toggle at any width.
 *  The offset rule is config/shell.topBarRightOffset (unit-tested). Mobile keeps the right
 *  edge: no panel docks there and the drawer is a full overlay. */
const CHAT_DRAWER_WIDTH = 480;
/** The drawer's wide reading mode (chat-drawer: `md:w-[min(880px,65vw)]`). */
const CHAT_DRAWER_WIDE = 'min(880px, 65vw)';

function TopBarCluster({ chatOpen, chatWide, onAsk, freshness }: {
  chatOpen: boolean;
  chatWide: boolean;
  onAsk: () => void;
  freshness: ReturnType<typeof useFreshness>;
}) {
  const { workspace } = useWorkspace();
  const { viewPreview } = useAuth();
  const panelWidth = useContextPanelWidth();
  // In wide mode the drawer is wider than 480px, so the row sits left of THAT width — the
  // view-preview exit must stay visible and clickable (review F5).
  const right = chatOpen && chatWide
    ? `calc(${topBarRightOffset(panelWidth, 0)}px + ${CHAT_DRAWER_WIDE})`
    : `${topBarRightOffset(panelWidth, chatOpen ? CHAT_DRAWER_WIDTH : 0)}px`;
  return (
    <div
      data-top-bar-cluster
      className="fixed top-4 right-3 md:right-[var(--top-bar-right)] z-30 flex items-center gap-2"
      style={{ '--top-bar-right': right } as React.CSSProperties}
    >
      <EnvironmentChip className="hidden md:inline-flex" />
      {workspace && workspace.id !== 'full' && (
        <span className="hidden md:inline-flex items-center px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/25">
          {workspace.name}
        </span>
      )}
      {/* SPEC-462/515: the view picker lives in the rail's account menu. While a preview
          is ACTIVE a status chip shows here, amber, so a previewing admin can never mistake
          the previewed view for their own and always has the exit in sight. */}
      {viewPreview && (
        <div className="hidden md:block">
          <ViewPreviewChip />
        </div>
      )}
      <div className="md:hidden">
        <SyncStatusIndicator freshness={freshness} />
      </div>
      {/* Mobile only — on desktop the rail's Decisions button replaces the pill. */}
      <div className="md:hidden">
        <RulingsPill />
      </div>
      {/* The Ask SuryaIQ trigger: last in the row, never shrinks; hidden while the drawer is
          open (the drawer's own header takes over). Mark only below md. */}
      {!chatOpen && <AskSuryaIQButton onClick={onAsk} />}
    </div>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPrefill, setChatPrefill] = useState<string | null>(null);
  const [chatWide, setChatWide] = useState(false);
  const { user: chatUser } = useAuth();
  const chatUserKey = chatUser?.username || 'anonymous';
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const freshness = useFreshness();
  // SPEC-462: the rail's search button opens the EXISTING sidebar search — expand the
  // sidebar if it is collapsed, then bump this token so the Sidebar focuses its input.
  const [searchFocusToken, setSearchFocusToken] = useState(0);
  const openSearch = useCallback(() => {
    setSidebarOpen(true);
    setSearchFocusToken((t) => t + 1);
  }, []);

  // SPEC-123: any page can open the copilot with a pre-filled question by dispatching
  // a `suryaiq:copilot-ask` window event ({detail:{text}}) — the scorecard tile
  // "Ask the assistant" footer uses this. Opening + prefilling here keeps pages
  // decoupled from the drawer (no prop drilling through the router).
  useEffect(() => {
    const onAsk = (e: Event) => {
      const text = (e as CustomEvent).detail?.text ?? '';
      setChatPrefill(text);
      setChatOpen(true);
    };
    window.addEventListener('suryaiq:copilot-ask', onAsk);
    return () => window.removeEventListener('suryaiq:copilot-ask', onAsk);
  }, []);

  return (
    <TourProvider>
    <RulingsProvider>
    <ContextPanelProvider>
    <FeedbackProvider chatOpen={chatOpen}>
    <NavLayoutProvider>
    <ArrangeMenuHost />
    {/* SPEC-511: `.app-shell` (index.css) is the viewport-sized, positioned, clipping frame. The
        document never scrolls; panes do. See the rule in index.css before changing it. */}
    <div className="app-shell flex">
      {/* Desktop: the left rail (SPEC-462) + the sidebar. The rail is persistent; collapsing
          the sidebar takes it to w-0 and leaves the rail, so global navigation never
          vanishes. The rail owns the show/hide control on desktop, so the sidebar gets no
          onToggle here (one control, not two). */}
      <div className="hidden md:flex h-full">
        <AppRail
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((v) => !v)}
          onSearch={openSearch}
          accountExtras={<ViewPicker />}
        />
        <Sidebar collapsed={!sidebarOpen} searchFocusToken={searchFocusToken} />
      </div>

      {/* Mobile overlay sidebar */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 md:hidden">
            <Sidebar collapsed={false} onToggle={() => setMobileMenuOpen(false)} onNavigate={() => setMobileMenuOpen(false)} />
          </div>
        </>
      )}

      {/* Mobile top-bar backdrop — a solid blurred band behind the fixed controls so
          page content scrolls UNDER a bar (Twitter-style) instead of colliding with
          floating buttons. Mobile only; sits beneath the z-30 controls. */}
      <div className="fixed top-0 inset-x-0 h-14 z-20 md:hidden bg-[var(--bg-base)]/85 backdrop-blur-md border-b border-[var(--border-subtle)] pointer-events-none" />

      {/* Mobile hamburger button */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        // SPEC-509: the walk-through picker returns focus here when it was opened from a
        // menu row that has since closed.
        data-mobile-menu-button
        aria-label="Open menu"
        className="fixed top-4 left-3 z-30 p-2 rounded-lg bg-[var(--bg-raised)] border border-[var(--border-subtle)] text-[var(--text-tertiary)] hover:text-amber-400 transition-colors shadow-lg md:hidden"
        title="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* SPEC-514: the top bar's right cluster (staging chip, workspace, view-as, Ask SuryaIQ). */}
      <TopBarCluster chatOpen={chatOpen} chatWide={chatWide} onAsk={() => setChatOpen(true)} freshness={freshness} />

      {/* Main column: the page (+ the SPEC-463 context panel on its right), then the bottom
          status bar (desktop). The chat drawer's right margin moved from <main> to this
          column so the bar and the panel shrink with it. */}
      <div className={`flex-1 min-w-0 flex flex-col transition-all duration-300 ease-in-out ${chatOpen ? 'md:mr-[480px]' : 'mr-0'}`}>
      <ContextPanelRow>
      <main className="flex-1 min-w-0 overflow-auto bg-[var(--bg-base)]">
        {/* Top padding clears the fixed top-4 control cluster on BOTH breakpoints —
            page-header actions previously shared its vertical band on desktop and
            overlapped it at mid widths (22 Jul Param screenshot). */}
        <div className="p-4 md:p-6 max-w-[1760px] mx-auto pt-14 md:pt-16">
          {/* Collapsed-sidebar section tabs (desktop only) — mobile keeps the
              hamburger overlay sidebar, which already carries the SectionNav. */}
          {!sidebarOpen && (
            <div className="hidden md:block">
              <CollapsedSectionBar />
            </div>
          )}
          {children}
        </div>
      </main>
      </ContextPanelRow>
      <div className="hidden md:block">
        <StatusBar lastSync={freshness.lastSync} syncAge={freshness.syncAge} syncStatus={freshness.syncStatus} />
      </div>
      </div>

      {/* Chat sidebar - hidden on mobile, floating button instead */}
      <div className="hidden md:block">
        {/* Fix loop 1: keyed by the signed-in user, so a login switch remounts the drawer
            with the new user's empty state — never a frame of the previous user's chat. */}
        <ChatSidebar key={chatUserKey} open={chatOpen} onToggle={() => setChatOpen(v => !v)} prefill={chatPrefill} onPrefillConsumed={() => setChatPrefill(null)} onWideChange={setChatWide} />
      </div>

      {/* SPEC-514: the mobile floating chat button is gone — the top bar's Ask SuryaIQ mark
          opens the assistant at every width (one control, not two). */}

      {/* Mobile chat overlay */}
      {chatOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setChatOpen(false)} />
          <div className="absolute inset-y-0 right-0 w-full max-w-sm">
            <ChatSidebar key={chatUserKey} open={true} onToggle={() => setChatOpen(false)} prefill={chatPrefill} onPrefillConsumed={() => setChatPrefill(null)} />
          </div>
        </div>
      )}

      {/* SPEC-145: guided walk-through picker (the tour overlay itself is rendered
          by TourProvider). */}
      <TourPicker />

      {/* SPEC-146: Rulings Desk — login-time decision wizard for Sudhin. */}
      <RulingsDesk />
    </div>
    </NavLayoutProvider>
    </FeedbackProvider>
    </ContextPanelProvider>
    </RulingsProvider>
    </TourProvider>
  );
}
